import styled, { css } from 'styled-components';
import {
  MouseEvent,
  Fragment,
  useState,
  useMemo,
  useCallback,
  Children,
  PropsWithChildren,
  ReactNode,
} from 'react';
import {
  bigStone,
  blueMana,
  darkerBlackPearl,
} from '@paradigm/design-system/src/colors';
import { Divider as DefaultDivider } from '#/components/UnifiedRfqs/drfq/common';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import VerticalDots from '@paradigm/design-system/src/assets/VerticalDots';
import ArrowDown from '@paradigm/design-system/src/assets/ArrowDown';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';

export type ColumnDef = PropsWithChildren<{
  readonly id: string;
  readonly name: string;
  readonly mobileOptions?: {
    // true if should be hidden in mobile view
    readonly isHidden?: boolean;
    // true if should always be displayed in the row
    readonly isMainRow?: boolean;
    // display only the value without the label
    readonly hideLabel?: boolean;
    readonly secondaryLabelContent?: ReactNode;
  };
  readonly desktopOptions?: {
    readonly isHidden?: boolean;
  };
}>;

interface ContainerProps {
  readonly columns: readonly ColumnDef[];
  readonly className?: string;
  readonly onContextMenu?: (args: { x: number; y: number }) => void;
  readonly dataTestId?: string;
}

/**
 *
 * Display responsive bottom table row.
 * - For desktop you should pass the grid-template-columns
 * @param props {ContainerProps}
 */
function BottomTableRow({
  dataTestId,
  onContextMenu,
  columns,
  className,
}: ContainerProps) {
  const isMobile = useIsMobile();

  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLElement | SVGSVGElement>) => {
      if (onContextMenu == null) return;
      event.stopPropagation();
      event.preventDefault();
      onContextMenu({ x: event.clientX, y: event.clientY });
    },
    [onContextMenu],
  );

  const mobileRow = (
    <MobileBottomTableRow
      dataTestId={dataTestId}
      handleContextMenu={handleContextMenu}
      columns={columns}
      className={className}
    />
  );

  const desktopRow = (
    <DesktopBottomTableRow
      columns={columns}
      dataTestId={dataTestId}
      className={className}
      handleContextMenu={handleContextMenu}
    />
  );

  return isMobile ? mobileRow : desktopRow;
}

interface ViewportProps {
  readonly columns: readonly ColumnDef[];
  readonly className?: string;
  readonly handleContextMenu: (
    event: MouseEvent<HTMLElement | SVGSVGElement>,
  ) => void;
  readonly dataTestId?: string;
}

function MobileBottomTableRow({
  dataTestId,
  handleContextMenu,
  columns,
  className,
}: ViewportProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const { mainCols, otherCols } = useMemo(
    () => groupColumnsForMobile(columns),
    [columns],
  );

  const mainColumns = useMemo(() => mainCols.map(renderColumn), [mainCols]);

  const cardColumns = useMemo(() => {
    return otherCols.map((column) => {
      if (isHiddenLabel(column)) {
        return <OnlyValue key={column.id}>{renderColumn(column)}</OnlyValue>;
      }

      return (
        <Fragment key={column.id}>
          <Label>
            {column.name}
            {column.mobileOptions?.secondaryLabelContent}
          </Label>
          <Value>{renderColumn(column)}</Value>
        </Fragment>
      );
    });
  }, [otherCols]);

  return (
    <div className={className} data-testid={dataTestId}>
      <MobileRowContainer
        isExpanded={isExpanded}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <MenuButton>
          <VerticalDots width={12} onClick={handleContextMenu} />
        </MenuButton>
        <MobileRowData role="button" className="mobile-bottom-table-row">
          {Children.toArray(mainColumns)}
        </MobileRowData>
        <ArrowIcon isOpen={isExpanded} width="12px" fill="#fff" />
      </MobileRowContainer>
      {isExpanded && <Divider />}
      <CardContainer isVisible={isExpanded}>
        <GridContainer>{cardColumns}</GridContainer>
      </CardContainer>
    </div>
  );
}

function DesktopBottomTableRow({
  columns,
  dataTestId,
  className,
  handleContextMenu,
}: ViewportProps) {
  const cells = useMemo(
    () =>
      columns
        .filter((column) => !(column.desktopOptions?.isHidden === true))
        .map(renderColumn),
    [columns],
  );

  return (
    <DesktopRow
      data-testid={dataTestId}
      onContextMenu={handleContextMenu}
      className={className}
    >
      {Children.toArray(cells)}
    </DesktopRow>
  );
}

function renderColumn({ children, id }: Pick<ColumnDef, 'children' | 'id'>) {
  const isNumberOrString =
    typeof children === 'string' || typeof children === 'number';
  return isNumberOrString ? <span aria-label={id}>{children}</span> : children;
}

function groupColumnsForMobile(columns: readonly ColumnDef[]): {
  mainCols: ColumnDef[];
  otherCols: ColumnDef[];
} {
  const mainCols = [];
  const otherCols = [];

  for (const column of columns) {
    if (column.mobileOptions?.isHidden === true) continue;
    if (column.mobileOptions?.isMainRow === true) {
      mainCols.push(column);
    } else {
      otherCols.push(column);
    }
  }

  // sort by columns that have no label
  otherCols.sort((a, b) => {
    if (isHiddenLabel(a)) return -1;
    if (isHiddenLabel(b)) return 1;
    return 0;
  });

  return { otherCols, mainCols };
}

const isHiddenLabel = (column: ColumnDef) =>
  column.mobileOptions?.hideLabel === true;

const ROW_HEIGHT = 42;
const MOBILE_ROW_HEIGHT = 52;
const ArrowIcon = styled(ArrowDown).withConfig<{ isOpen: boolean }>({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'none')};
  opacity: 0.6;
  transition: transform 0.3s ease-in-out;
`;

export const DesktopRow = styled.div`
  display: grid;
  gap: 19px;
  align-items: center;
  padding: 0 22px;
  border-bottom: 1px solid ${bigStone};
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  height: ${ROW_HEIGHT}px;
  color: #b5b9bd;
  background-color: #051524;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${MOBILE_ROW_HEIGHT}px;
  }
`;

const CardContainer = styled.div<{ isVisible: boolean }>`
  background-color: ${darkerBlackPearl};
  padding: 1rem;
  width: 100%;
  font-size: 1rem;
  transition: max-height 0.3s ease, padding 0.3s;
  box-sizing: border-box;
  overflow: hidden;
  ${({ isVisible }) =>
    isVisible
      ? css`
          max-height: 1000px;
          border-bottom: 1px solid ${bigStone};
        `
      : css`
          max-height: 0;
          padding-top: 0;
          padding-bottom: 0;
        `};
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;
`;

const Label = styled.label<{ disabled?: boolean }>`
  color: ${blueMana};
  margin: 0;

  ${({ disabled = false }) => disabled && 'opacity: 0.5;'}
`;

const OnlyValue = styled.div`
  grid-column: span 2;
`;

const Value = styled.div`
  color: white;
  opacity: 0.7;
  font-weight: 400;
  justify-self: end;
`;

const MenuButton = styled.button`
  background-color: transparent;
  border: none;
  padding: 1rem;
  padding-left: 0.5rem;
`;

export const Divider = styled(DefaultDivider)`
  background-color: ${darkerBlackPearl};
  width: 100%;
`;

const MobileRowContainer = styled.div<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  padding-right: 1rem;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
  border: 0;
  color: white;
  background-color: ${darkerBlackPearl};
  ${({ isExpanded }) =>
    isExpanded
      ? 'border-bottom: 1px solid transparent;'
      : `border-bottom: 1px solid ${bigStone};`}
  height: ${ROW_HEIGHT}px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${MOBILE_ROW_HEIGHT}px;

    .strategy-description-content {
      text-align: start;
    }
  }
`;

const MobileRowData = styled.div`
  flex: 1;
  opacity: 0.6;
  gap: 0.875rem;
  padding-right: 0.875rem;
  background-color: ${darkerBlackPearl};
  align-items: center;
  display: flex;
  font-size: 1rem;
  grid-auto-flow: column;
`;

export default BottomTableRow;
